/* eslint-disable @typescript-eslint/no-explicit-any, no-await-in-loop, no-nested-ternary */
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { logger } from '@musedrops/shared';
import { formatDateTime } from '../../../shared/components/DateTimeField';
import { PageToolbar } from '../../../shared/components/PageToolbar';
import { JsonContentViewer } from '../../../shared/components/JsonContentViewer';
import { getEdgeFunctionAuthHeaders } from '../../../services/supabase';

// Base URL for Edge Functions
const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

interface LogFile {
  name: string;
  updated_at: string;
  metadata: Record<string, any>;
}

export const BlobLogsTab = () => {
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<
    Record<string, unknown> | { rawText: string } | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const notify = useNotify();

  // Load initial file list on mount
  useEffect(() => {
    loadLogFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to load log files from storage via Edge Function
  const loadLogFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${EDGE_FUNCTION_URL}/logs-list?limit=10&sortBy=updated_at&sortOrder=desc`,
        {
          method: 'GET',
          headers: await getEdgeFunctionAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load log files');
      }

      const { files } = await response.json();
      setLogFiles(files || []);

      // Clear selection if selected file is no longer in the list
      if (
        selectedFile &&
        !files.some((f: LogFile) => f.name === selectedFile)
      ) {
        setSelectedFile(null);
        setFileContent(null);
      }
    } catch (error) {
      logger.error('Error loading log files', { error });
      notify('Failed to load log files', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Function to load content of a selected log file via Edge Function
  const loadFileContent = async (fileName: string) => {
    setLoadingContent(true);
    setSelectedFile(fileName);
    try {
      const response = await fetch(
        `${EDGE_FUNCTION_URL}/logs-download?fileName=${encodeURIComponent(fileName)}`,
        {
          method: 'GET',
          headers: await getEdgeFunctionAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load file content');
      }

      const text = await response.text();

      // Try to parse as JSON
      try {
        const json = JSON.parse(text);
        setFileContent(json);
      } catch {
        // If not valid JSON, show as text
        setFileContent({ rawText: text });
      }
    } catch (error) {
      logger.error('Error loading file content', { fileName, error });
      notify('Failed to load file content', { type: 'error' });
      setFileContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  // Function to clear all logs via Edge Function
  const handleClearLogs = async () => {
    setIsClearing(true);
    try {
      // Get list of all files with pagination to ensure we get everything
      const allFiles: LogFile[] = [];
      let offset = 0;
      const limit = 1000; // Max per page
      let hasMore = true;

      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(
          `${EDGE_FUNCTION_URL}/logs-list?limit=${limit}&offset=${offset}&sortBy=updated_at&sortOrder=desc`,
          {
            method: 'GET',
            headers: await getEdgeFunctionAuthHeaders(),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to list log files');
        }

        const { files } = await response.json();

        if (!files || files.length === 0) {
          hasMore = false;
        } else {
          allFiles.push(...files);

          // If we got fewer files than the limit, we've reached the end
          if (files.length < limit) {
            hasMore = false;
          } else {
            offset += limit;
          }
        }
      }

      if (allFiles.length > 0) {
        // Delete all files
        const filePaths = allFiles.map(f => f.name);
        const deleteResponse = await fetch(`${EDGE_FUNCTION_URL}/logs-delete`, {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({ fileNames: filePaths }),
        });

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json();
          throw new Error(errorData.error || 'Failed to delete log files');
        }
      }

      notify('All logs cleared successfully', { type: 'success' });

      // Reset state
      setLogFiles([]);
      setSelectedFile(null);
      setFileContent(null);
    } catch (error) {
      logger.error('Error clearing logs', { error });
      notify('Failed to clear logs', { type: 'error' });
    } finally {
      setIsClearing(false);
      setClearDialogOpen(false);
    }
  };

  // Format file date as "time ago" for the list
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Show "now" for items less than 1 minute old
    if (diffInSeconds < 60) {
      return 'now';
    }

    // Get relative time and remove "about", "over", "almost" prefixes
    let relativeTime = formatDistanceToNow(date, { addSuffix: true });
    relativeTime = relativeTime
      .replace(/^about /, '')
      .replace(/^over /, '')
      .replace(/^almost /, '');

    return relativeTime;
  };

  // Format file name for display (remove timestamp prefix and .json suffix, add emojis)
  const formatFileName = (fileName: string) => {
    // Check if filename starts with timestamp pattern: YYYYMMDD-HHMMSS-
    const timestampPattern = /^\d{8}-\d{6}-/;
    let displayName = fileName;

    if (timestampPattern.test(fileName)) {
      // Remove the timestamp prefix
      displayName = fileName.replace(timestampPattern, '');
    }

    // Remove .json suffix if present
    if (displayName.endsWith('.json')) {
      displayName = displayName.slice(0, -5);
    }

    // Add emoji based on prefix
    let emoji = '⚪️'; // default
    if (displayName.startsWith('req')) {
      emoji = '🔵';
    } else if (displayName.startsWith('res')) {
      emoji = '🟢';
    }

    return `${emoji} ${displayName}`;
  };

  // Format file size for display
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with buttons */}
      <PageToolbar
        left={
          <>
            <Button
              variant="outlined"
              onClick={loadLogFiles}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setClearDialogOpen(true)}
              disabled={loading || logFiles.length === 0}
            >
              Clear
            </Button>
          </>
        }
      />

      {/* Main content area */}
      <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0 }}>
        {/* Left column - File list */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : logFiles.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No log files found
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {logFiles.map(file => (
                    <ListItem key={file.name} disablePadding>
                      <ListItemButton
                        selected={selectedFile === file.name}
                        onClick={() => loadFileContent(file.name)}
                      >
                        <ListItemText
                          primary={formatFileName(file.name)}
                          secondary={
                            <>
                              {formatTimeAgo(file.updated_at)}
                              {' • '}
                              {formatSize(file.metadata.size)}
                            </>
                          }
                          primaryTypographyProps={{
                            sx: {
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              wordBreak: 'break-all',
                            },
                          }}
                          secondaryTypographyProps={{
                            sx: { fontSize: '0.75rem' },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Right column - Content viewer */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {selectedFile || 'Select a file to view contents'}
              </Typography>
              {selectedFile && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {formatDateTime(
                    logFiles.find(f => f.name === selectedFile)?.updated_at ||
                      ''
                  )}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 2,
              }}
            >
              {loadingContent ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !selectedFile ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Select a log file from the list to view its contents
                  </Typography>
                </Box>
              ) : fileContent ? (
                <JsonContentViewer content={fileContent} showToolbar />
              ) : (
                <Alert severity="error">Failed to load file content</Alert>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Clear confirmation dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title">Clear All Logs?</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-dialog-description">
            This will permanently delete all log files in the storage bucket.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearDialogOpen(false)}
            disabled={isClearing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearLogs}
            color="error"
            variant="contained"
            disabled={isClearing}
          >
            {isClearing ? 'Clearing...' : 'Clear All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
